import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MetaHead } from '../components/MetaHead';
import { COMPANY_INFO, COMMON_CONTACT_CALLOUT } from '../data/scrapedData';

export function AfgiftningPage() {
  const location = useLocation();

  // Form state
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setFormSubmitted(true);
        form.reset();
      } else {
        setFormError('Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch {
      setFormError('Der opstod en netværksfejl. Ring venligst på 75 17 20 80.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.replace('#', '');
      const element = document.getElementById(targetId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Kom ud af stof- eller alkoholmisbrug | Midtgaarden",
    "description": "Få afgiftning tilpasset din situation hos Midtgaarden. Vi tilbyder dag- og døgnbehandling med mere end 20 års erfaring. Kontakt os i dag.",
    "publisher": {
      "@type": "Organization",
      "name": "Den Selvejende Institution Midtgaarden",
      "url": "https://www.midtgaarden.dk"
    }
  };

  return (
    <>
      <MetaHead
        title="Kom ud af stof- eller alkoholmisbrug | Midtgaarden"
        description="Få afgiftning tilpasset din situation hos Midtgaarden. Vi tilbyder dag- og døgnbehandling med mere end 20 års erfaring. Kontakt os i dag."
        ogImage="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/to-unge-maend-spiller-pool-i-aktivitetsrum.jpg"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* ========================================================================= */}
      {/* 1. HERO SECTION */}
      {/* ========================================================================= */}
      <section className="relative bg-[#1C1C1E] text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/to-unge-maend-spiller-pool-i-aktivitetsrum.jpg"
            alt="To unge mænd spiller pool ved et grønt billardbord i et aktivitetsrum"
            className="w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1E] via-[#1C1C1E]/90 to-[#1C1C1E]/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 [text-shadow:_0_2px_12px_rgba(0,0,0,0.85)]">
          <div className="max-w-3xl space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#3885B8] text-white">
                <i className="fa-solid fa-capsules text-[11px]" aria-hidden="true" />
                Afgiftning & Behandling
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white/90 backdrop-blur-xs">
                Stofmisbrug & Alkoholmisbrug
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Afgiftning og alkoholbehandling med plads til hele din situation
            </h1>

            <div className="space-y-4 text-base sm:text-lg md:text-xl text-white/90 leading-relaxed font-normal">
              <p>
                Hos Midtgaarden tilbyder vi professionel afgiftning og misbrugsbehandling til mennesker, som ønsker støtte til at ændre et stofmisbrug eller et skadeligt alkoholforbrug. Fra vores center i Sejstrup nær Esbjerg hjælper vi mennesker fra hele landet med individuelt tilrettelagt behandling, hvor hele livssituationen ses i sammenhæng.
              </p>
            </div>

            {/* CTA action buttons */}
            <div className="pt-2 flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href={COMPANY_INFO.phoneTel}
                className="h-14 px-8 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] active:bg-[#1F557B] text-white font-bold text-base shadow-lg transition-all duration-200 inline-flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3885B8]"
              >
                <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring: {COMPANY_INFO.phone}</span>
              </a>

              <a
                href={COMPANY_INFO.emailMailto}
                aria-label="Send e-mail til Midtgaarden"
                className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white flex items-center justify-center transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white shrink-0"
              >
                <i className="fa-solid fa-envelope text-lg" aria-hidden="true" />
              </a>
            </div>

            {/* Trust highlights */}
            <div className="pt-6 flex flex-wrap sm:flex-nowrap justify-between items-center gap-y-3 border-t border-white/15 text-xs sm:text-sm text-white/80">
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8] shrink-0" aria-hidden="true" />
                <span>Psykiatrisk overlæge</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8] shrink-0" aria-hidden="true" />
                <span>NADA-øreakupunktur</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8] shrink-0" aria-hidden="true" />
                <span>20+ års erfaring</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8] shrink-0" aria-hidden="true" />
                <span>Døgn- og dagtilbud</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 2. SEKTION: AFGIFTNING VED STOFMISBRUG */}
      {/* ========================================================================= */}
      <section id="stofmisbrug" className="py-16 sm:py-24 bg-white border-b border-[#1C1C1E]/10 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header & badge */}
          <div className="max-w-3xl mb-12">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8]">
                STOFMISBRUG & MEDICINSK UDTRAPNING
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Afgiftning er det første skridt mod en stoffri tilværelse
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <p className="text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
              Mange mennesker er af den opfattelse, at problemet for en afhængig ikke er at blive stoffri, men derimod at forblive stoffri. Ikke desto mindre er afgiftning alligevel det første skridt mod en tilværelse som stoffri. Hos Den Selvejende Institution{' '}
              <Link
                to="/"
                className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
              >
                Midtgaarden
              </Link>{' '}
              tilbyder vi professionel afgiftning for personer med et misbrug.
            </p>
          </div>

          {/* Medical Planning & Intake Details with side-by-side photo */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
            <div className="lg:col-span-7 space-y-5 text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed">
              <p>
                Afgiftning af beboere planlægges i samråd med vores overlæge i psykiatri, Julius Nissen. Det er rart for alle parter, hvis vi på forhånd kan få mange og præcise oplysninger om en kommende beboers forbrug – både det ordinerede og det illegale. På denne måde har vi mulighed for at planlægge den optimale afgiftning af kroppen. Det indebærer, at vi også gerne vil have alle relevante oplysninger fra visiterende myndighed, som omhandler forbruget.
              </p>
              <p>
                Varigheden på afgiftning på vores institution kan variere meget fra person til person – afhængigt af stoftype, varighed, mængde og andre faktorer. Typisk varer en afgiftning mellem 2-6 uger, men kan både være kortere og længere.
              </p>
              <p>
                Medicinen vi anvender til afgiftning, er ordinær afgiftningsmedicin – typisk metadon, subutex og risolid. I særlige tilfælde, hvor lægen finder det nødvendigt, bruger vi også andre benzodiazepin- eller morfinpræparater til afgiftning.
              </p>
              <p>
                De behandlingsmæssige temaer under afgiftning handler især om erkendelse af omfang og konsekvenser i forbindelse med brug af stoffer. Beboeren introduceres også til Midtgaardens måde at opfatte misbrug – både som tilstand og behandlingen heraf.
              </p>
            </div>

            <div className="lg:col-span-5 space-y-5">
              <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-xl bg-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/to-personer-spiller-pool-ved-groent-billardbord.jpg"
                  alt="To personer ved et billardbord i et aktivitetsrum"
                  className="w-full h-56 object-cover rounded-none"
                />
                <div className="p-4 bg-white rounded-none border-t border-[#1C1C1E]/10">
                  <div className="text-xs text-[#1C1C1E]/70 flex items-start gap-2">
                    <i className="fa-solid fa-location-dot text-[#3885B8] mt-0.5 shrink-0" aria-hidden="true" />
                    <span>Midtgaarden i Sejstrup: rolige rammer og fast struktur under afrusning og afgiftning.</span>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-none bg-[#F8FAFC] border border-[#1C1C1E]/10 border-l-4 border-l-[#3885B8] space-y-2 shadow-xs">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#3885B8]">
                  <i className="fa-solid fa-notes-medical" aria-hidden="true" />
                  <span>Tryghed & faglighed</span>
                </div>
                <p className="text-xs sm:text-sm text-[#1C1C1E]/75 leading-relaxed">
                  Afgiftningsforløbet skræddersys altid til den enkelte med fokus på både fysisk restitution og psykisk aflastning.
                </p>
              </div>
            </div>
          </div>

          {/* Abstinenser, omsorg og fysisk aktivitet */}
          <div className="bg-[#F8FAFC] rounded-none border border-[#1C1C1E]/10 p-8 sm:p-10 lg:p-12 shadow-xs">
            <div className="max-w-3xl mb-8">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-[1px] bg-[#3885B8]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#3885B8]">Abstinenshåndtering & omsorg</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#1C1C1E]">
                Støtte i den sværeste overgangsfase
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed">
              <div className="space-y-4">
                <p>
                  De sidste uger på medicin og de første uger uden, er som regel den periode, hvor beboeren er værst plaget af abstinenser. Dette viser sig oftest som uro, appetitløshed, svedeture, søvnproblemer, rastløshed og mangel på energi.
                </p>
                <p>
                  Det er derfor, i denne periode, ekstra vigtigt med støtte og omsorg, og beboerne opfordres til at have ekstra meget kontakt til både personale og andre beboere. Det er vigtigt, at beboeren oplever rammer med ro og tryghed.
                </p>
              </div>

              <div className="space-y-4">
                <p>
                  Ligesom det også er vigtigt med rigelige doser af motion og frisk luft for at komme sig så hurtigt som muligt. Beboerne opfordres derfor til at deltage i både dagsprogram og fysiske aktiviteter i det omfang de kan.
                </p>
                <p>
                  Alt erfaring viser nemlig, at jo hurtigere stoffernes eftervirkninger bliver brændt ud af kroppen, jo hurtigere får man det bedre. Efter afgiftningsforløbet starter primærbehandlingen, som strækker sig over en længere periode end selve afgiftningen (læs mere om vores samlede{' '}
                  <Link
                    to="/behandling"
                    className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                  >
                    behandlingsforløb
                  </Link>
                  ).
                </p>
              </div>
            </div>

            {/* 4 Pillars of Substance Detox */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8 pt-8 border-t border-[#1C1C1E]/10">
              <div className="p-6 rounded-none bg-white border border-[#1C1C1E]/10 shadow-xs hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-clock" aria-hidden="true" />
                </div>
                <h4 className="text-sm font-bold text-[#1C1C1E] mb-1">2-6 ugers varighed</h4>
                <p className="text-xs text-[#1C1C1E]/70 leading-relaxed">
                  Individuelt tilpasset afhængigt af stoftype, mængde og den enkeltes tilstand.
                </p>
              </div>

              <div className="p-6 rounded-none bg-white border border-[#1C1C1E]/10 shadow-xs hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-user-doctor" aria-hidden="true" />
                </div>
                <h4 className="text-sm font-bold text-[#1C1C1E] mb-1">Lægeansvar</h4>
                <p className="text-xs text-[#1C1C1E]/70 leading-relaxed">
                  Medicinsk planlægning under Julius Nissen med metadon, subutex og risolid.
                </p>
              </div>

              <div className="p-6 rounded-none bg-white border border-[#1C1C1E]/10 shadow-xs hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-syringe" aria-hidden="true" />
                </div>
                <h4 className="text-sm font-bold text-[#1C1C1E] mb-1">Daglig NADA</h4>
                <p className="text-xs text-[#1C1C1E]/70 leading-relaxed">
                  Øreakupunktur mod abstinenser, uro og søvnproblemer af uddannet personale.
                </p>
              </div>

              <div className="p-6 rounded-none bg-white border border-[#1C1C1E]/10 shadow-xs hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-person-running" aria-hidden="true" />
                </div>
                <h4 className="text-sm font-bold text-[#1C1C1E] mb-1">Aktivitet & motion</h4>
                <p className="text-xs text-[#1C1C1E]/70 leading-relaxed">
                  Motion og frisk luft i Sejstrup fremmer kroppens naturlige udskillelse.
                </p>
              </div>
            </div>

            {/* Next step link */}
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 p-6 rounded-none bg-[#3885B8]/10 border border-[#3885B8]/20 border-l-4 border-l-[#3885B8]">
              <div className="text-xs sm:text-sm text-[#1C1C1E]">
                <strong className="text-[#3885B8]">Næste skridt efter afgiftning:</strong> Primærbehandlingen strækker sig over ca. 6 måneder og sikrer det langsigtede arbejde med stoffrihed.
              </div>
              <Link
                to="/behandling/primaerbehandling"
                className="px-6 py-3 rounded-none bg-[#3885B8] text-white text-xs sm:text-sm font-bold uppercase tracking-wider hover:bg-[#2A6E9B] transition-colors whitespace-nowrap shadow-xs inline-flex items-center gap-2"
              >
                <span>Læs om primærbehandling</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 3. VISUELT AFBRÆK / BILLEDBÅND (Stemning & Omsorg) */}
      {/* ========================================================================= */}
      <section className="relative overflow-hidden min-h-[380px] sm:min-h-[460px] md:min-h-[500px] flex items-end bg-[#1C1C1E]">
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/personer-forsyner-sig-fra-buffet-ved-spisebord.jpg"
          alt="Personer forsyner sig med mad fra en anrettet buffet ved et spisebord"
          className="absolute inset-0 w-full h-full object-cover object-center brightness-95 contrast-[1.03] rounded-none"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1E]/95 via-[#1C1C1E]/45 to-[#1C1C1E]/20" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 text-white">
          <div className="max-w-2xl space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#3885B8] text-white">
              <i className="fa-solid fa-heart text-[11px]" aria-hidden="true" />
              Trygge rammer i Sejstrup
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-4">
              Et støttende miljø med plads til ro, restitution og forandring
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-xl">
              Afgiftning og afrusning handler om at give krop og sind den nødvendige ro til at starte forfra i trygge og professionelle omgivelser.
            </p>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 4. SEKTION: ALKOHOLBEHANDLING OG AFGIFTNING */}
      {/* ========================================================================= */}
      <section id="alkoholbehandling" className="py-16 sm:py-24 bg-[#F8FAFC] border-b border-[#1C1C1E]/10 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header / Intro afsnit */}
          <div className="max-w-3xl mb-12">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8]">
                ALKOHOLMISBRUG & HELHEDSORIENTERET STØTTE
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Alkoholbehandling med plads til hele din situation
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <p className="text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
              Hos Midtgaarden tilbyder vi alkoholbehandling til mennesker, som ønsker støtte til at ændre et skadeligt alkoholforbrug. Fra vores center i Sejstrup nær Esbjerg hjælper vi mennesker fra hele landet med individuelt tilrettelagt misbrugsbehandling, hvor hele livssituationen bliver set i sammenhæng.
            </p>
          </div>

          {/* Hvordan foregår alkoholbehandling i Danmark? */}
          <div className="bg-white rounded-none border border-[#1C1C1E]/10 p-8 sm:p-10 lg:p-12 mb-12 shadow-xs">
            <div className="max-w-3xl mb-8">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-[1px] bg-[#3885B8]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#3885B8]">Behandlingsforløbet</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#1C1C1E] mb-4">
                Hvordan foregår alkoholbehandling i Danmark?
              </h3>
              <div className="space-y-4 text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed">
                <p>
                  Spørgsmålet om hvordan foregår alkoholbehandling i Danmark har ikke ét svar, fordi behovet varierer fra person til person. Et forløb begynder normalt med en afklaring af alkoholforbrug, helbred, psykisk trivsel, sociale forhold og tidligere behandling, så indsatsen kan planlægges på et fagligt grundlag.
                </p>
                <p>
                  Hos os kan behandlingen blandt andet omfatte afgiftning, primærbehandling, samtaler, dagbehandling, døgnbehandling og tilbagefaldsforebyggelse. Behovet for afgiftning skal vurderes fagligt, da abstinenser efter et stort alkoholforbrug i nogle tilfælde kræver sundhedsfaglig hjælp.
                </p>
              </div>
            </div>

            {/* To strukturerede trin */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-[#1C1C1E]/10">
              <div className="p-6 rounded-none bg-[#F8FAFC] border border-[#1C1C1E]/10 border-l-4 border-l-[#3885B8] shadow-xs hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-clipboard-check" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-[#1C1C1E] mb-2">
                  Først afklarer vi, hvilken støtte du har brug for
                </h4>
                <p className="text-xs sm:text-sm text-[#1C1C1E]/80 leading-relaxed">
                  Ved den første afklaring ser vi på, hvad alkoholen gør ved din hverdag, dine relationer og din evne til at fastholde aftaler og ansvar. Vi undersøger også, om der er behov for en mere beskyttet ramme. Du behøver derfor ikke kende den rette behandlingsform, før du tager kontakt.
                </p>
              </div>

              <div className="p-6 rounded-none bg-[#F8FAFC] border border-[#1C1C1E]/10 border-l-4 border-l-[#3885B8] shadow-xs hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-chart-line" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-[#1C1C1E] mb-2">
                  Planen kan udvikle sig sammen med dig
                </h4>
                <p className="text-xs sm:text-sm text-[#1C1C1E]/80 leading-relaxed">
                  En behandlingsplan skal kunne følge din udvikling. Derfor arbejder vi med differentierede forløb, som kan rumme forskellige behov undervejs. Den biopsykosociale tilgang betyder, at vi inddrager fysiske, psykiske og sociale forhold frem for kun at se på selve alkoholforbruget.
                </p>
              </div>
            </div>
          </div>

          {/* Hvad indeholder et sammenhængende behandlingsforløb? */}
          <div className="bg-white rounded-none border border-[#1C1C1E]/10 p-8 sm:p-10 lg:p-12 shadow-xs">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
              
              <div className="lg:col-span-7">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-[1px] bg-[#3885B8]" />
                  <span className="text-xs font-bold uppercase tracking-wider text-[#3885B8]">Faglig model & indhold</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#1C1C1E] mb-4">
                  Hvad indeholder et sammenhængende behandlingsforløb?
                </h3>
                <p className="text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed mb-8">
                  Med mere end 20 års erfaring arbejder vi med forløb, der kan strække sig fra afgiftning og primærbehandling til efterværn og udslusning. Behandlingen har afsæt i CENAPS og den biopsykosociale model, og fokus er at skabe forståelse for misbruget og risikoen for tilbagefald.
                </p>

                {/* 4 Elementer fra listen */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-5 rounded-none bg-[#F8FAFC] border border-[#1C1C1E]/10 flex items-start gap-4 hover:border-[#3885B8]/40 transition-colors shadow-2xs">
                    <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center shrink-0 text-lg">
                      <i className="fa-solid fa-user-check" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#1C1C1E] mb-1">Individuel vurdering</h4>
                      <p className="text-xs sm:text-sm text-[#1C1C1E]/75 leading-relaxed">
                        En individuel vurdering af alkoholforbrug, trivsel, ressourcer og behov for støtte.
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-none bg-[#F8FAFC] border border-[#1C1C1E]/10 flex items-start gap-4 hover:border-[#3885B8]/40 transition-colors shadow-2xs">
                    <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center shrink-0 text-lg">
                      <i className="fa-solid fa-building-user" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#1C1C1E] mb-1">Afstemt behandlingsform</h4>
                      <p className="text-xs sm:text-sm text-[#1C1C1E]/75 leading-relaxed">
                        En behandlingsform, der afstemmes med behovet for dagbehandling eller døgnophold, eventuelt med opfølgende støtte i et{' '}
                        <Link
                          to="/botilbud"
                          className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                        >
                          botilbud
                        </Link>
                        .
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-none bg-[#F8FAFC] border border-[#1C1C1E]/10 flex items-start gap-4 hover:border-[#3885B8]/40 transition-colors shadow-2xs">
                    <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center shrink-0 text-lg">
                      <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#1C1C1E] mb-1">Tilbagefald & hverdagsstruktur</h4>
                      <p className="text-xs sm:text-sm text-[#1C1C1E]/75 leading-relaxed">
                        Arbejde med tilbagefald, relationer, hverdagsstruktur og vejen videre efter behandlingen.
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-none bg-[#F8FAFC] border border-[#1C1C1E]/10 flex items-start gap-4 hover:border-[#3885B8]/40 transition-colors shadow-2xs">
                    <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center shrink-0 text-lg">
                      <i className="fa-solid fa-diagram-project" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#1C1C1E] mb-1">Helhedsorienteret indsats</h4>
                      <p className="text-xs sm:text-sm text-[#1C1C1E]/75 leading-relaxed">
                        Mulighed for en helhedsorienteret indsats ved samtidige psykiske og sociale problemstillinger – herunder behandling af{' '}
                        <Link
                          to="/dobbeltdiagnose"
                          className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                        >
                          dobbeltdiagnose
                        </Link>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Side photo card */}
              <div className="lg:col-span-5 space-y-4">
                <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-xl bg-white">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/mand-i-samtale-i-laenestol-under-ovenlys.jpg"
                    alt="Mand i samtale siddende i en lænestol under et ovenlysvindue"
                    className="w-full h-56 object-cover rounded-none"
                  />
                  <div className="p-4 bg-white rounded-none border-t border-[#1C1C1E]/10">
                    <p className="text-xs text-[#1C1C1E]/75">
                      <i className="fa-solid fa-circle-check text-[#3885B8] mr-1.5" aria-hidden="true" />
                      Erfarne rammer i Sejstrup nær Esbjerg med plads til at genopbygge en sund hverdag.
                    </p>
                  </div>
                </div>

                <div className="p-5 rounded-none bg-[#F8FAFC] border border-[#1C1C1E]/10 border-l-4 border-l-[#3885B8] flex items-center justify-between gap-4 shadow-2xs">
                  <div className="text-xs sm:text-sm text-[#1C1C1E]/80">
                    Læs mere om den teoretiske ramme bag vores metoder.
                  </div>
                  <Link
                    to="/behandling/bio-psyko-sociale-model"
                    className="text-xs font-bold uppercase tracking-wider text-[#3885B8] hover:text-[#2A6E9B] whitespace-nowrap inline-flex items-center gap-1.5"
                  >
                    <span>Den biopsykosociale model</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </Link>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 5. KONTAKT & FORMULAR SEKTION (Kopi fra Forsiden) */}
      {/* ========================================================================= */}
      <section id="kontakt-raadgivning" className="relative py-20 sm:py-28 bg-[#1C1C1E] text-white overflow-hidden scroll-mt-20">
        {/* Full-bleed background image with dark overlay */}
        <img
          src="https://assets.auxodev.dk/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/loungeomraade-med-laenestole-under-skraavaegge.jpg"
          alt="Loungeområde med sorte lænestole og sofabord under skråvægge med synlige bjælker"
          className="absolute inset-0 w-full h-full object-cover opacity-60 rounded-none"
        />
        <div className="absolute inset-0 bg-[#1C1C1E]/50 backdrop-blur-xs" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Heading, text, contact links */}
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#3885B8]">
                KONTAKT MIDTGAARDEN
              </span>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-4">
                {COMMON_CONTACT_CALLOUT.header}
              </h2>

              <p className="text-base text-white/80 leading-relaxed">
                Har du brug for at få hjælp til at komme ud af misbrug, eller er du i kontakt med en, som har det? Så er du meget velkommen til at ringe hertil på telefon{' '}
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="text-white font-medium underline underline-offset-2 hover:text-[#3885B8] transition-colors whitespace-nowrap"
                >
                  {COMPANY_INFO.phone}
                </a>{' '}
                eller sende en e-mail til{' '}
                <a
                  href={COMPANY_INFO.emailMailto}
                  className="text-white font-medium underline underline-offset-2 hover:text-[#3885B8] transition-colors whitespace-nowrap"
                >
                  {COMPANY_INFO.email}
                </a>{' '}
                og høre mere om døgnbehandling og Midtgaarden generelt.
              </p>

              <p className="text-xs text-white/60 italic">
                Vi besvarer alle henvendelser hurtigt, fortroligt og uforpligtende.
              </p>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white text-white font-semibold text-sm hover:bg-white hover:text-[#1C1C1E] transition-all"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Ring: {COMPANY_INFO.phone}</span>
                </a>

                <a
                  href={COMPANY_INFO.emailMailto}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white/80 font-semibold text-sm hover:border-white hover:text-white transition-all"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>{COMPANY_INFO.email}</span>
                </a>
              </div>
            </div>

            {/* Right Column: Webform structured vertically */}
            <div className="lg:col-span-6 bg-white/5 p-8 rounded-none border border-white/15 backdrop-blur-md shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-6">Send en besked</h3>

              {formSubmitted ? (
                <div className="p-6 bg-[#3885B8]/20 border border-[#3885B8] rounded-none text-center space-y-3">
                  <i className="fa-solid fa-circle-check text-3xl text-[#3885B8]" aria-hidden="true" />
                  <h4 className="text-lg font-bold text-white">Mange tak for din henvendelse</h4>
                  <p className="text-xs text-white/80">Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  
                  {/* Honeypot Antispam Field */}
                  <div className="sr-only" aria-hidden="true">
                    <label htmlFor="website_url">Website URL</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  {/* Top row 50/50 grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                        Navn *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="Dit navn"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        placeholder="Dine 8 cifre"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email field */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                      E-mailadresse *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="din@email.dk"
                      className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Description / Message field */}
                  <div>
                    <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                      Beskrivelse *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      required
                      placeholder="Skriv din besked eller spørgsmål her..."
                      className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors resize-none"
                    ></textarea>
                  </div>

                  {formError && (
                    <p className="text-xs font-semibold text-rose-400">{formError}</p>
                  )}

                  {/* Primary CTA button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto px-8 py-3.5 rounded-none bg-[#3885B8] hover:bg-[#2A6E9B] text-white font-bold text-sm tracking-wider uppercase transition-colors shadow-lg cursor-pointer inline-flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3885B8] disabled:opacity-50"
                  >
                    {submitting ? 'Sender henvendelse...' : 'Send henvendelse'}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
